import React, { useContext, useEffect, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { sumFormFormatter } from '@shared/helpers/sumFormFormatter';
import { Switcher } from '@shared/ui';
import { DistributeAmount } from '@widgets/AddPayments/ui/AddPaymentModal/DistributeAmount/DistributeAmount';

import cls from './EditPaymentBody.module.css';
import { DateInput } from '../../../../../components/DateInput/DateInput';
import { Editable } from '../../../../../components/UI/Editable/Editable';
import { FileInput } from '../../../../../components/UI/FileInput/FileInput';
import { Form } from '../../../../../components/UI/Form/Form';
import { FormBlock } from '../../../../../components/UI/Form/FormBlock/FormBlock';
import { Select } from '../../../../../components/UI/Select/Select';
import { Textarea } from '../../../../../components/UI/Textarea/Textarea';
import { Input } from '../../../../../components/UI/TextInput/Input';
import { Context } from '../../PaymentsList';
import { ModalContext } from '../EditPaymentModal';

export const EditPaymentBody = ({ setIsOpenPartner }) => {
    const { t } = useTranslation();
    const {
        formState,
        setFormState,
        errors,
        isBetweenAccount,
        setIsBetweenAccount,
        selectedAccount,
        selectedSecondAccount,
    } = useContext(ModalContext);
    const { currentPayment } = useContext(Context);

    const allAccounts = useSelector((state) => state.paymentReducer.accountsSelectList);
    const allExpense = useSelector((state) => state.paymentReducer.expenseList);
    const allIncome = useSelector((state) => state.paymentReducer.incomeList);
    const allPartners = useSelector((state) => state.paymentReducer.partnersSelectList);
    const allEstimates = useSelector((state) => state.paymentReducer.estimatesSelectList);

    const isCorrespondentIntegration = currentPayment.correspondent
        ? !!currentPayment.correspondent.integration
        : false;

    const isIntegrationDisabled =
        !!currentPayment.account.integration || isCorrespondentIntegration;

    const partnersListWithButton = [
        {
            title: t(
                'PaymentsList.EditPaymentModal.EditPaymentBody.partnersListWithButton.notPicked.text'
            ),
            value: null,
        },
        {
            title: t(
                'PaymentsList.EditPaymentModal.EditPaymentBody.partnersListWithButton.addCounterAgent.text'
            ),
            isButton: true,
            buttonHandler: () => {
                setFormState((prevState) => ({ ...prevState, partners: null }));
                setIsOpenPartner(true);
            },
            value: '',
        },
        ...allPartners,
    ];

    const [images, setImages] = useState('');

    const onPaymentDateChange = (newDate) => {
        const date = moment(new Date(newDate)).utc().format('DD.MM.YYYY');
        setFormState((prevState) => ({ ...prevState, paymentDate: date }));
    };

    const onAccrualDateChange = (newDate) => {
        const date = moment(new Date(newDate)).utc().format('DD.MM.YYYY');
        setFormState((prevState) => ({ ...prevState, accrualDate: date }));
    };

    const selectedExpense = allExpense.find((account) => account.value === formState.expenditures);
    const selectedIncome = allIncome.find((account) => account.value === formState.expenditures);
    const selectedPartners = allPartners.find((account) => account.value === formState.partners);
    const selectedEstimate = allEstimates.find((account) => account.value === formState.estimates);

    useEffect(() => {
        if (!currentPayment.account.integration && selectedPartners) {
            setFormState((prev) => ({ ...prev, fullName: selectedPartners.fullName }));
        }
    }, [selectedPartners]);

    const inputHandler = (event) => {
        const { name, value } = event.target;

        setFormState((prevState) => ({ ...prevState, [name]: value }));
    };

    const sumFormHandler = (event) => {
        const { name, value } = event.target;

        const formattedValue = sumFormFormatter(value);

        setFormState((prevState) => ({
            ...prevState,
            [name]: formattedValue,
        }));
    };

    const selectAccountHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, account: value }));
    };
    const selectSecondAccountHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, firstStepAccount: value }));
    };

    const selectExpenditureHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, expenditures: value }));
    };
    const selectPartnersHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, partners: value }));
    };
    const selectEstimatesHandler = (value) => {
        setFormState((prevState) => ({ ...prevState, estimates: value }));
    };

    const handleForm = (e) => {
        const images = e.currentTarget.files;
        setFormState((prevState) => ({ ...prevState, invoicePayment: Object.values(images) }));
        const inputText = Object.values(images)
            .map((image) => image.name)
            .join(', ');
        setImages(inputText);
    };

    const oldFilesHandler = (id) => {
        const newFilesList = formState.oldFiles.filter((file) => file.id !== id);
        setFormState((prev) => ({ ...prev, oldFiles: newFilesList }));
    };

    return (
        <Form>
            <div className={classNames(cls.bodyWrapper)}>
                <FormBlock>
                    <Select
                        selected={selectedAccount}
                        onChange={selectAccountHandler}
                        placeholder={t(
                            'PaymentsList.EditPaymentModal.EditPaymentBody.SelectAccount.placeholder'
                        )}
                        title={t(
                            'PaymentsList.EditPaymentModal.EditPaymentBody.SelectAccount.title'
                        )}
                        isRequired
                        isAccount
                        options={allAccounts.filter((account) => !account.account.integration)}
                        error={errors.account}
                        isDisabled={
                            isIntegrationDisabled ||
                            currentPayment.account.is_archive ||
                            currentPayment.is_locked_for_editing
                        }
                    />
                    {currentPayment.type === 'transfer' ? (
                        <Input
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.InputTransfer.title'
                            )}
                            name="sum"
                            value={formState.sum}
                            onChange={sumFormHandler}
                            icon={selectedAccount.account.currency.sign}
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                            error={errors.sum}
                        />
                    ) : (
                        <Input
                            isRequired
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.InputPurposePayment.title'
                            )}
                            name="purposePayment"
                            value={formState.purposePayment}
                            onChange={(event) => inputHandler(event)}
                            error={errors.purposePayment}
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                        />
                    )}
                </FormBlock>
                <FormBlock>
                    <div className={classNames(cls.blockWrapper)}>
                        <DateInput
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.DateInput.paymentDate.title'
                            )}
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                            isRequired
                            date={formState.paymentDate}
                            inventoryGuid="inventoryGuid"
                            parameterName="paymentDate"
                            newCallback={onPaymentDateChange}
                            options={{
                                startDate: formState.paymentDate,
                                singleDatePicker: true,
                                showDropdowns: true,
                                autoApply: true,
                            }}
                        />
                    </div>
                    <div className={classNames(cls.blockWrapper)}>
                        <DateInput
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.DateInput.accrualDate.title'
                            )}
                            isRequired
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                            date={formState.accrualDate}
                            inventoryGuid="inventoryGuid"
                            parameterName="accrualDate"
                            newCallback={onAccrualDateChange}
                            options={{
                                startDate: formState.accrualDate,
                                singleDatePicker: true,
                                showDropdowns: true,
                                autoApply: true,
                            }}
                        />
                    </div>
                </FormBlock>
                <div className={classNames(cls.switcherWrapper)}>
                    <Switcher
                        classes={cls.switcher}
                        isDisabled
                        onClick={() => setIsBetweenAccount((prev) => !prev)}
                        isActive={isBetweenAccount}
                        title={t(
                            'PaymentsList.EditPaymentModal.EditPaymentBody.Switcher.isBetweenAccount.title'
                        )}
                    />
                </div>
                {currentPayment.type !== 'transfer' && (
                    <FormBlock>
                        <div className={classNames(cls.blockWrapper)}>
                            <Input
                                isRequired
                                title={t(
                                    'PaymentsList.EditPaymentModal.EditPaymentBody.InputSum.title'
                                )}
                                name="sum"
                                value={formState.sum}
                                onChange={sumFormHandler}
                                icon={currentPayment.account.currency.sign}
                                isDisabled={
                                    isIntegrationDisabled ||
                                    currentPayment.account.is_archive ||
                                    currentPayment.is_locked_for_editing
                                }
                                error={errors.sum}
                            />
                        </div>
                        <div className={classNames(cls.blockWrapper)}>
                            {currentPayment.type !== 'transfer' ? (
                                <Select
                                    isSmartPosition
                                    selected={
                                        currentPayment.type === 'credit'
                                            ? selectedExpense
                                            : selectedIncome
                                    }
                                    onChange={selectExpenditureHandler}
                                    placeholder={t(
                                        'PaymentsList.EditPaymentModal.EditPaymentBody.SelectNotTransfer.notPicked'
                                    )}
                                    title={`${t(
                                        'PaymentsList.EditPaymentModal.EditPaymentBody.SelectNotTransfer.item.title'
                                    )} ${
                                        currentPayment.type === 'credit'
                                            ? t(
                                                  'PaymentsList.EditPaymentModal.EditPaymentBody.SelectNotTransfer.item.outcome.title'
                                              )
                                            : t(
                                                  'PaymentsList.EditPaymentModal.EditPaymentBody.SelectNotTransfer.item.income.title'
                                              )
                                    }`}
                                    isRequired
                                    options={
                                        currentPayment.type === 'credit' ? allExpense : allIncome
                                    }
                                    isDisabled={
                                        currentPayment.account.is_archive ||
                                        currentPayment.is_locked_for_editing
                                    }
                                    error={errors.paymentItem}
                                />
                            ) : (
                                <div />
                            )}
                        </div>
                    </FormBlock>
                )}
                {!isBetweenAccount ? (
                    <>
                        <FormBlock>
                            <Select
                                isSmartPosition
                                selected={selectedPartners}
                                onChange={selectPartnersHandler}
                                placeholder={t(
                                    'PaymentsList.EditPaymentModal.EditPaymentBody.SelectPartners.placeholder'
                                )}
                                title={t(
                                    'PaymentsList.EditPaymentModal.EditPaymentBody.SelectPartners.title'
                                )}
                                isRequired
                                options={partnersListWithButton}
                                error={errors.account}
                                isDisabled={
                                    currentPayment.account.is_archive ||
                                    currentPayment.is_locked_for_editing
                                }
                            />
                            <Input
                                isRequired={false}
                                title={t(
                                    'PaymentsList.EditPaymentModal.EditPaymentBody.InputPartners.title'
                                )}
                                name="fullName"
                                value={formState.fullName}
                                onChange={(event) => inputHandler(event)}
                                error={errors.fullName}
                                isDisabled={
                                    currentPayment.account.is_archive ||
                                    currentPayment.is_locked_for_editing
                                }
                                isDisabled
                            />
                        </FormBlock>

                        <>
                            {formState.distributeEstimates.length <= 1 && (
                                <FormBlock>
                                    <Select
                                        isSmartPosition
                                        selected={selectedEstimate || null}
                                        onChange={selectEstimatesHandler}
                                        placeholder={t(
                                            'PaymentsList.EditPaymentModal.EditPaymentBody.SelectNotTransfer.notPicked'
                                        )}
                                        title={t(
                                            'PaymentsList.EditPaymentModal.EditPaymentBody.SelectedEstimatesSelect.title'
                                        )}
                                        options={allEstimates}
                                        isDisabled={
                                            currentPayment.account.is_archive ||
                                            currentPayment.is_locked_for_editing
                                        }
                                        error={
                                            !formState.distributeEstimates.length && errors.estimate
                                        }
                                    />
                                </FormBlock>
                            )}
                            <FormBlock>
                                <DistributeAmount
                                    isDisabled={
                                        currentPayment.account.is_archive ||
                                        currentPayment.is_locked_for_editing
                                    }
                                    formState={formState}
                                    setFormState={setFormState}
                                    error={
                                        !!formState.distributeEstimates.length && errors.estimate
                                    }
                                />
                            </FormBlock>
                        </>
                    </>
                ) : (
                    <FormBlock>
                        <Select
                            isSmartPosition
                            selected={selectedSecondAccount}
                            onChange={selectSecondAccountHandler}
                            placeholder={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.SelectedSecondAccountSelect.placeholder'
                            )}
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.SelectedSecondAccountSelect.title'
                            )}
                            isAccount
                            isRequired
                            options={allAccounts.filter((account) => !account.account.integration)}
                            error={errors.account}
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                        />
                        <Editable
                            secondName="receivedSum"
                            mainName="sum"
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                selectedAccount.account.currency.sign ===
                                    selectedSecondAccount.account.currency.sign ||
                                currentPayment.is_locked_for_editing
                            }
                            valueMain={formState.sum}
                            valueSecond={formState.receivedSum}
                            handler={sumFormHandler}
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.Editable.receivedSum.title'
                            )}
                            icon={selectedSecondAccount.account.currency.sign}
                            error={errors.receiveSum}
                            active
                        />
                    </FormBlock>
                )}
                {currentPayment.type === 'transfer' && (
                    <FormBlock>
                        <Input
                            isRequired
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.InputPurposePayment.title'
                            )}
                            name="purposePayment"
                            value={formState.purposePayment}
                            onChange={(event) => inputHandler(event)}
                            error={errors.purposePayment}
                            isDisabled={
                                isIntegrationDisabled ||
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                        />
                    </FormBlock>
                )}
                <FormBlock>
                    <div className={classNames(cls.blockWrapper)}>
                        <Textarea
                            isRequired={false}
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.TextArea.comment.title'
                            )}
                            name="comment"
                            value={formState.comment}
                            isDisabled={
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                            onChange={(event) => inputHandler(event)}
                        />
                    </div>
                    <div className={classNames(cls.blockWrapper)}>
                        <FileInput
                            fileText={images}
                            oldFiles={formState.oldFiles}
                            isRequired={false}
                            title={t(
                                'PaymentsList.EditPaymentModal.EditPaymentBody.FileInput.invoicePayment'
                            )}
                            error={errors.attachments}
                            name="invoicePayment"
                            value={formState.invoicePayment}
                            onChange={handleForm}
                            oldFilesHandler={oldFilesHandler}
                            isDisabled={
                                currentPayment.account.is_archive ||
                                currentPayment.is_locked_for_editing
                            }
                        />
                    </div>
                </FormBlock>
            </div>
        </Form>
    );
};
